import React, { useState, useRef, useEffect } from "react";
import { FileText } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./button";
import { Textarea } from "./textarea";

export interface DescriptionProps {
  placeholder?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  onSave?: (value: string) => void;
  onClear?: () => void;
  className?: string;
  hideButtons?: boolean;
  disabled?: boolean;
}

export const Description = React.forwardRef<HTMLDivElement, DescriptionProps>(
  (
    {
      placeholder = "Add description",
      value = "",
      onValueChange,
      onSave,
      onClear,
      className,
      hideButtons = false,
      disabled = false,
    },
    ref
  ) => {
    const [isEditing, setIsEditing] = useState(false);
    const [internalValue, setInternalValue] = useState(value);
    const [fixedHeight, setFixedHeight] = useState<number | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Update internal state when value prop changes
    React.useEffect(() => {
      setInternalValue(value);
      // Only exit editing mode if this is a true external change (not from user input)
      // When hideButtons is true and we're editing, don't exit editing mode on value changes
      if (!hideButtons || !isEditing) {
        setIsEditing(false);
        setFixedHeight(null);
      }
    }, [value, hideButtons, isEditing]);

    const handleClick = () => {
      if (!isEditing && !disabled) {
        // Capture the exact height of the button containing the description
        if (value.trim() !== "" && buttonRef.current) {
          const buttonHeight = buttonRef.current.getBoundingClientRect().height;
          setFixedHeight(buttonHeight);
        }

        setIsEditing(true);
        setInternalValue(value);
      }
    };

    // Auto-focus the textarea when entering edit mode
    useEffect(() => {
      if (isEditing && textareaRef.current) {
        textareaRef.current.focus();
        // Place cursor at end of content for existing text
        if (value.trim() !== "") {
          textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
        }
      }
    }, [isEditing, value]);

    // Handle click outside to cancel editing (restore original value)
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (isEditing && containerRef.current && !containerRef.current.contains(event.target as Node)) {
          // Cancel editing - restore original value
          setInternalValue(value);
          setIsEditing(false);
          setFixedHeight(null);

          // For timeline bar use case, also reset the value change
          if (hideButtons) {
            onValueChange?.(value);
          }
        }
      };

      if (isEditing) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [isEditing, value, hideButtons, onValueChange]);

    const handleSave = () => {
      onSave?.(internalValue);
      onValueChange?.(internalValue);
      // Always exit editing mode after save
      setIsEditing(false);
      setFixedHeight(null);
    };

    // Check if save button should be disabled
    const isSaveDisabled = internalValue.trim() === "" || internalValue === value;

    const handleClear = () => {
      setInternalValue("");
      onValueChange?.("");
      onClear?.();
      setIsEditing(false);
      setFixedHeight(null);
    };

    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);

      // If hideButtons is true, call onValueChange immediately for timeline bar use case
      if (hideButtons) {
        onValueChange?.(newValue);
      }
    };

    if (isEditing) {
      // Determine the appropriate height for the textarea
      const getTextareaHeight = () => {
        if (fixedHeight !== null) {
          // For existing content, maintain the display height
          return `${Math.max(fixedHeight, 100)}px`;
        } else if (value.trim() === "") {
          // For new/empty content, start at 100px
          return "100px";
        } else {
          // Fallback to minimum height
          return "126px";
        }
      };

      return (
        <div
          ref={(node) => {
            if (containerRef.current !== node) {
              (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }
          }}
          className={cn("w-full space-y-3", className)}
        >
          <Textarea
            ref={textareaRef}
            value={internalValue}
            onChange={handleTextareaChange}
            placeholder={placeholder}
            style={{
              height: getTextareaHeight(),
              minHeight: value.trim() === "" ? "100px" : getTextareaHeight(),
            }}
            className="text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-3 py-2 resize-none overflow-y-auto whitespace-pre-wrap rounded-lg"
          />
          {!hideButtons && (
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={handleClear}>
                Clear
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isSaveDisabled}>
                Save
              </Button>
            </div>
          )}
        </div>
      );
    }

    // If there's saved content, show it as readable text with hover effect
    if (value.trim() !== "") {
      return (
        <div ref={ref} className={cn("w-full", className)}>
          <button
            ref={buttonRef}
            onClick={handleClick}
            disabled={disabled}
            className={cn(
              "w-full text-left px-3 py-2 rounded-lg bg-transparent transition-colors",
              disabled ? "cursor-default" : "hover:bg-muted cursor-pointer"
            )}
          >
            <div className="text-sm text-foreground whitespace-pre-wrap">{value}</div>
          </button>
        </div>
      );
    }

    return (
      <div ref={ref} className={cn("w-full", className)}>
        <button
          onClick={handleClick}
          disabled={disabled}
          className={cn(
            "w-full flex items-center gap-1 h-[30px] px-3 py-1 rounded-lg bg-transparent text-left transition-colors",
            disabled ? "cursor-default" : "hover:bg-muted cursor-pointer"
          )}
        >
          <FileText className="w-3 h-3 text-muted-foreground shrink-0" />
          <span className="text-xs text-muted-foreground leading-4">{placeholder}</span>
        </button>
      </div>
    );
  }
);

Description.displayName = "Description";
